import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, CalendarCheck, Phone, Instagram, Globe2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { scrollToSection } from "./scroll";
import { useAuth } from "../../auth/AuthContext";
import { useBooking } from "../../context/BookingContext";
import { useSiteData } from "../../context/SiteData";
import { TRANSLATED_SLUGS } from "../../data/seoPages";
import { SUPPORTED_LOCALES } from "../../data/i18n";

const LINKS = [
  { label: "Home", hash: "#home" },
  { label: "Tours", hash: "#tours" },
  { label: "Services", hash: "#services" },
  { label: "Destinations", hash: "#destinations" },
  { label: "About", hash: "#about" },
  { label: "Guide", hash: "#guide" },
  { label: "Contact", hash: "#contact" },
];

const LANGS = ["en", "de", "fr", "ar"];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openBooking } = useBooking();
  const { settings, locale, content } = useSiteData();
  const navigate = useNavigate();

  const go = (hash) => {
    setOpen(false);
    if (hash === "#guide") {
      navigate("/blog");
      window.scrollTo(0, 0);
      return;
    }
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(hash), 150);
    } else {
      scrollToSection(hash);
    }
  };

  const switchLocale = (loc) => {
    setOpen(false);
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (SUPPORTED_LOCALES.includes(parts[0])) parts.shift();
    const rest = parts.join("/");
    // Only the hub pages have translations; everything else lands on the localized home
    const path = loc === "en" ? `/${rest}` : TRANSLATED_SLUGS.includes(rest) ? `/${loc}/${rest}` : `/${loc}`;
    navigate(path === "/" ? "/" : path.replace(/\/$/, ""));
    window.scrollTo(0, 0);
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    toast.success("Signed out. See you on the road!");
  };

  const isAdmin = user && user.role === "admin";
  const accountPath = isAdmin ? "/admin" : "/my-reservations";
  const accountLabel = isAdmin ? "Admin Panel" : "My Trips";
  const AccountIcon = isAdmin ? LayoutDashboard : CalendarCheck;

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-white/75 backdrop-blur-xl border-b border-slate-200/70"
    >
      {settings.promo_enabled && settings.promo_text ? (
        <div className="bg-[var(--brand)] text-white text-center text-xs sm:text-sm font-bold py-2 px-4" data-testid="promo-banner">
          {settings.promo_text}
        </div>
      ) : null}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center gap-2 sm:gap-3">
        <RouterLink
          to="/"
          data-testid="nav-logo"
          onClick={(e) => {
            if (window.location.pathname === "/") {
              e.preventDefault();
              scrollToSection("#home");
            }
          }}
          className="flex items-center group"
          aria-label="Voyage Switzerland — homepage"
        >
          <img src="/logo.png" alt="Voyage Switzerland" className="h-12 sm:h-14 w-auto" data-testid="nav-logo-img" />
        </RouterLink>

        <div className="flex items-center gap-1.5 sm:gap-2 ms-1 sm:ms-3 shrink-0" data-testid="nav-contact-links">
          <a
            href={`tel:${(content.contact_phone || "").replace(/\s+/g, "")}`}
            data-testid="nav-phone-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 sm:px-3.5 py-2 text-[11px] sm:text-xs font-bold text-[#0b132b] transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)] whitespace-nowrap"
          >
            <Phone className="w-3.5 h-3.5 text-[var(--brand)] shrink-0" />
            {content.contact_phone}
          </a>
          <a
            href="https://www.instagram.com/voyageswisscom/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-link"
            aria-label="Voyage Switzerland on Instagram"
            className="inline-flex w-9 h-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
          >
            <Instagram className="w-4 h-4" />
          </a>
        </div>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.hash)}
              className="text-sm font-semibold text-slate-600 hover:text-[var(--brand)] transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden md:block relative" data-testid="lang-switcher">
            <button
              data-testid="lang-toggle"
              onClick={() => setLangOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
              aria-label="Change language"
              aria-expanded={langOpen}
            >
              <Globe2 className="w-3.5 h-3.5 text-[var(--brand)]" />
              {locale}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute end-0 top-full mt-2 w-32 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl z-50" data-testid="lang-menu">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => { switchLocale(l); setLangOpen(false); }}
                    className={`w-full text-start rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                      locale === l ? "bg-[var(--brand)] text-white" : "text-slate-500 hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {user ? (
            <>
              <span className="hidden xl:inline-flex items-center rounded-full bg-[var(--brand-soft)] px-4 py-1.5 text-xs font-bold text-[var(--brand)]" data-testid="nav-user-chip">
                {user.name}
              </span>
              <button
                data-testid="nav-account-button"
                onClick={() => navigate(accountPath)}
                className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/25 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
              >
                <AccountIcon className="w-4 h-4" />
                {accountLabel}
              </button>
              <button
                data-testid="nav-logout-button"
                onClick={handleLogout}
                className="hidden sm:flex w-10 h-10 rounded-xl border border-slate-200 items-center justify-center text-slate-500 hover:text-red-500 hover:border-red-200 transition-colors"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="nav-sign-in-button"
                onClick={() => navigate("/login")}
                className="hidden sm:inline-flex items-center rounded-full border-2 border-[#0b132b]/10 bg-white text-sm font-bold px-5 py-2 text-[#0b132b] transition-all duration-300 hover:border-[var(--brand)] hover:text-[var(--brand)]"
              >
                Sign In
              </button>
              <button
                data-testid="nav-book-now-button"
                onClick={() => openBooking()}
                className="hidden sm:inline-flex items-center rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-2.5 shadow-lg shadow-[var(--brand)]/25 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:shadow-[var(--brand)]/40 hover:-translate-y-0.5"
              >
                Book Now
              </button>
            </>
          )}
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-[#0b132b]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-xl px-5 py-4 flex flex-col gap-1" data-testid="nav-mobile-panel">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.hash)}
              className="text-start text-sm font-semibold text-slate-700 py-2.5 px-3 rounded-xl hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-2 px-3 py-2" data-testid="lang-switcher-mobile">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">Language</span>
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-mobile-${l}`}
                onClick={() => switchLocale(l)}
                className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                  locale === l ? "bg-[var(--brand)] text-white" : "text-slate-400 hover:text-[var(--brand)]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-2 pt-2">
            {user ? (
              <>
                <button
                  data-testid="nav-mobile-account-button"
                  onClick={() => {
                    setOpen(false);
                    navigate(accountPath);
                  }}
                  className="w-full text-start text-sm font-bold text-[var(--brand)] py-2.5 px-3 rounded-xl hover:bg-[var(--brand-soft)] transition-colors"
                >
                  {accountLabel}
                </button>
                <button
                  data-testid="nav-mobile-logout-button"
                  onClick={handleLogout}
                  className="w-full text-start text-sm font-semibold text-red-500 py-2.5 px-3 rounded-xl hover:bg-red-50 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                data-testid="nav-mobile-sign-in-button"
                onClick={() => {
                  setOpen(false);
                  navigate("/login");
                }}
                className="w-full text-start text-sm font-bold text-[var(--brand)] py-2.5 px-3 rounded-xl hover:bg-[var(--brand-soft)] transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
